import {
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';
import {z} from 'zod';

import {
  SmtpCredentialsResponseBody,
  SmtpEmailProviderCode,
  usePatchEmailSmtpCredentialsMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

interface ChangeEmailPasswordFormProps {
  credentials: SmtpCredentialsResponseBody;
  providerCode: SmtpEmailProviderCode;
}

export function ChangeEmailPasswordForm(props: ChangeEmailPasswordFormProps) {
  const [patchCredentials] = usePatchEmailSmtpCredentialsMutation();
  const providerCode = props.providerCode ?? 'custom-smtp';

  const handleSubmit: FormSubmitHandler<ChangeEmailPasswordFormSchemaType> = async (values) => {
    await patchCredentials({
      id: props.credentials.id,
      patchSmtpCredentialsRequestBody: {
        password: values.password,
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.passwordChanged'));
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<ChangeEmailPasswordFormSchemaType>
      onSubmit={handleSubmit}
      experimentalZodSchema={ChangeEmailPasswordFormSchema}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            name="password"
            type="password"
            label={i18n.t('entity.emailService.labels.password')}
            control={control}
            tooltip={i18n.t(`entity.emailService.labels.${providerCode}-passwordTooltip`)}
            isRequired
          />

          <HStack spacing={2} justify="flex-end">
            <FormButton
              control={control}
              type="button"
              title={i18n.t('general.actions.discard')}
              variant="secondary"
              onClick={closeCurrentDialog}
            />
            <FormButton control={control} type="submit" title={i18n.t('general.actions.save')} />
          </HStack>
        </VStack>
      )}
    </Form>
  );
}

const ChangeEmailPasswordFormSchema = z.object({
  password: z.string().min(1),
});

type ChangeEmailPasswordFormSchemaType = z.infer<typeof ChangeEmailPasswordFormSchema>;
